<script lang="ts">
  import { onDestroy, onMount } from 'svelte';

  import { AppState, ToastQueue } from '../etc/stores';
  import { Backend } from '../services/backend';
  import { Container } from '../services/container';
  import Page from '../components/misc/Page.svelte';
  import TransactionItem from '../components/misc/TransactionItem.svelte';
  import Spinner from '../components/misc/Spinner.svelte';
  import { Logger } from '../services/logger';
  import { _ } from 'svelte-i18n';
  import HeaderInternal from '../components/misc/HeaderInternal.svelte';
  import { getMonthAbbrev, getParamsVisualTest } from '../etc/utils';
  import Analytics from '../services/analytics';
  import { mockTransactions } from '../mocks/mockVisualTest';
  import Text from '../components/typography/Text.svelte';
  import { useLocation, useNavigate } from 'svelte-navigator';
  import { useFinishSignIn } from '../etc/hooks/useModalInternal';
  import { Auth } from '../services/auth';
  import { SessionManager } from '../services/session-manager';
  import { mockProvisionalTransaction } from '../mocks/mockProvisionalTransaction';
  import sessionKeys from '../etc/sessionKeys';
  import { fade } from 'svelte/transition';

  const auth = Container.get(Auth);
  const backend = Container.get(Backend);
  const session = Container.get(SessionManager) as SessionManager;
  const logger = Container.get(Logger).context('Transactions.svelte') as Logger;

  const location = useLocation();
  const navigate = useNavigate();

  const { open, close } = useFinishSignIn();

  type TransactionsProps = {
    [key: string]: any[];
  };

  let transactions: TransactionsProps = {};
  let months = [];
  let loading = false;
  let target;
  let container;
  let next = '0';

  let handleNavigationBackPage;
  let timeoutAnimation;
  let startAnimation = false;

  const navAnimation: boolean = $location.state?.navAnimation;

  const t = (text: string) => {
    return $_(`transactions.${text}`);
  };

  const getMoreTransactions = async (loadPage?: boolean) => {
    if (next && !loading) {
      try {
        loading = true;

        let response = null;
        if (getParamsVisualTest().enabled) response = mockTransactions;
        else
          response = await backend.listTransactions(
            $AppState.business.id,
            next
          );

        if (loadPage) {
          AppState.update(state => ({
            ...state,
            website: {
              ...state.website,
              showNotificationTransaction: false
            }
          }));

          const checkout = session.get(
            sessionKeys.CHECKOUT_WITHOUT_TRANSACTION
          );

          if (checkout) {
            if (!checkout.showedNotification) {
              const nameSessionFull = session.getKey(
                sessionKeys.CHECKOUT_WITHOUT_TRANSACTION
              );
              const checkoutFullStorage = JSON.parse(
                localStorage.getItem(nameSessionFull)
              );

              localStorage.setItem(
                nameSessionFull,
                JSON.stringify({
                  ...checkoutFullStorage,
                  value: {
                    ...checkoutFullStorage.value,
                    showedNotification: true
                  }
                })
              );
            }

            response.items.push({
              updatedAt: checkout.createdAt,
              ...mockProvisionalTransaction
            });
          }
        }

        response.items.forEach(transaction => {
          const month = getMonthAbbrev(transaction.updatedAt);

          if (!transactions[month]) {
            transactions[month] = [];
          }

          transactions[month].push(transaction);
        });

        months = Object.keys(transactions);

        if (response.links.next) {
          next = response.links.next.split('skip=')[1].split('&')[0];
        } else {
          next = null;
        }
        return true;
      } catch (err) {
        logger.error(err);
        ToastQueue.add({
          title: $_('error.title'),
          message: t('error'),
          type: 'error',
          duration: 5000
        });
      } finally {
        loading = false;
      }
    }
  };

  const onBack = () => {
    close();
    if (navAnimation) {
      clearTimeout(timeoutAnimation);
      startAnimation = true;
      timeoutAnimation = setTimeout(() => {
        if (handleNavigationBackPage) handleNavigationBackPage();
        navigate(-1);
      }, 600);
    } else {
      navigate(-1);
    }
  };

  const createObserver = () => {
    const observer = new IntersectionObserver(
      async entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            getMoreTransactions();
          }
        });
      },
      { root: container, rootMargin: '50px' }
    );

    if (target) {
      observer.observe(target);
    }
  };

  onMount(async () => {
    AppState.update(state => ({
      ...state,
      website: {
        ...state.website,
        lightIcon: false
      }
    }));

    if (await auth.isSoftLogin()) {
      open();
    } else {
      await getMoreTransactions(true);
      createObserver();
    }
    Analytics.logPageView('My transactions', '/transactions');
  });

  onDestroy(() => {
    clearTimeout(timeoutAnimation);
  });
</script>

<HeaderInternal
  title={t('title')}
  {onBack}
  {navAnimation}
  {startAnimation}
  bind:handleNavigationBackPage
/>
<Page padding="0" hideCloseButton headerInternal>
  <div
    class="transactions-container"
    bind:this={container}
    class:--animation={startAnimation}
    class:centered={loading || months.length === 0}
    in:fade={{ duration: navAnimation ? 200 : 0 }}
  >
    {#if months.length > 0}
      <div class="transactions-group-wrapper">
        {#each months as month}
          <div class="transactions-group">
            <div class="transactions-month">
              <Text
                class="transactions-month-text"
                component="h3"
                type="small"
                bold>{month}</Text
              >
            </div>
            <div class="transactions-list">
              {#each transactions[month] as transaction}
                <div class="transactions-item">
                  <TransactionItem data={transaction} />
                </div>
              {/each}
            </div>
          </div>
        {/each}
        <div class="transactions-group-end-bullet" />
        <div bind:this={target} class="load-more">
          {#if loading}
            <Spinner color="#6B6B6B" />{/if}
        </div>
      </div>
    {:else if loading}
      <div class="transactions-container__loading">
        <Spinner color="#6B6B6B" />
      </div>
    {:else}
      <div class="transactions-container__empty">
        <Text type="medium" color="#949DB6" textAlign="center">
          {t('empty')}
        </Text>
      </div>
    {/if}
  </div>
</Page>

<style lang="scss">
  :global(.transactions-month-text) {
    font-size: 14px !important;
    line-height: 16px !important;
    font-weight: 700 !important;
  }

  .transactions-container {
    overflow: auto;
    height: 100%;
    width: calc(100% - 48px);
    max-height: 420px;
    box-sizing: content-box;
    padding: 32px 24px 0;
    display: flex;
    transition: opacity 0.3s ease-in-out;

    &.--animation {
      opacity: 0;
    }

    &.centered {
      align-items: center;
      justify-content: center;
    }

    &__loading {
      display: flex;
      justify-content: center;
    }
    &__empty {
      padding: 0 40px;
    }
  }

  .transactions {
    &-group-end-bullet {
      width: 10px;
      height: 10px;
      background: var(--twism-theme-background);
      border-radius: 10px;
      margin-top: 8px;
    }

    &-group-wrapper {
      width: 100%;
    }
    &-group {
      position: relative;
      padding-bottom: 35px;

      &:before {
        content: '';
        border-left: 1px solid #ececec;
        position: absolute;
        left: 5px;
        top: 20px;
        bottom: 0;
      }
    }

    &-group + &-group {
      margin-top: 6px;
    }

    &-list {
      padding-left: 20px;
    }

    &-item {
      margin-top: 16px;
    }

    &-month {
      display: flex;
      align-items: center;
      &:before {
        content: '';
        width: 10px;
        height: 10px;
        background: var(--twism-theme-background);
        border-radius: 10px;
        margin-right: 10px;
      }
    }
  }

  .load-more {
    text-align: center;
  }
  @media screen and (max-width: 468px) {
    .transactions-container {
      height: 100vh;
      max-height: calc(100vh - 150px);
      justify-content: center;
    }
  }
</style>
